import {
  TypeGuard,
  WithRef,
  isDocRef,
  isEnumValue,
  isObject,
  isWithRef,
  type DocumentReference,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import {
  AutomationTemplateKey,
  IAutomation,
  IAutomationCustomTemplate,
  isAutomation,
  isAutomationConfiguration,
  type IAutomationConfiguration,
  type IAutomationResource,
} from '../automation/automation';
import { isAutomationTiming } from '../automation/automation-timing';
import { type ITemplateDefinition } from '../templating/template-definition';

export interface IAutomatedNotificationConfiguration
  extends IAutomationConfiguration,
    Omit<IAutomatedNotification, 'customTemplate'> {
  templateRef: DocumentReference<ITemplateDefinition>;
  automationTemplate?: AutomationTemplateKey;
}

export function isAutomatedNotificationConfiguration(
  data: unknown
): data is IAutomatedNotificationConfiguration {
  const extraPropertiesGuard = TypeGuard.interface<
    Pick<IAutomatedNotificationConfiguration, 'templateRef'>
  >({
    templateRef: isDocRef,
  });
  return (
    isAutomationConfiguration(data) &&
    isAutomatedNotification(data) &&
    extraPropertiesGuard(data)
  );
}

export function getAutomatedNotificationFromConfig(
  config: IAutomatedNotificationConfiguration
): IAutomatedNotification {
  return {
    name: config.name,
    subject: config.subject,
    type: config.type,
    timing: config.timing,
    requiredConditions: config.requiredConditions,
  };
}

export interface IAutomatedNotification extends IAutomationResource {
  name: string;
  subject: string;
  type: AutomatedNotificationType;
  customTemplate?: IAutomationCustomTemplate;
}

export enum AutomatedNotificationType {
  SMS = 'sms',
  EMAIL = 'email',
}

export enum AutomationContext {
  Appointment = 'appointment',
  Marketing = 'marketing',
}

export const AUTOMATED_NOTIFICATION_TYPES: AutomatedNotificationType[] = [
  AutomatedNotificationType.EMAIL,
  AutomatedNotificationType.SMS,
];

export function isAutomatedNotification(
  data: unknown
): data is IAutomatedNotification {
  return (
    isObject(data) &&
    isString(data.name) &&
    isAutomationTiming(data.timing) &&
    'type' in data &&
    isEnumValue(AutomatedNotificationType, data.type)
  );
}

export function isNotificationAutomation(
  automation: unknown
): automation is WithRef<IAutomation<IAutomatedNotification>> {
  return (
    isWithRef(automation) &&
    isAutomation<IAutomatedNotification>(automation, isAutomatedNotification)
  );
}
